import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
} from '@mui/material';
import {
  AccountBalance,
  CompareArrows,
  ChildCare,
  Receipt,
} from '@mui/icons-material';
import SEOHead from '../components/SEO/SEOHead';
import PageHeader from '../components/Layout/PageHeader';
import AtomicAnswer from '../components/SEO/AtomicAnswer';
import FAQSection from '../components/SEO/FAQSection';
import ArticulosRelacionadosQuebec from '../components/TaxSeason/ArticulosRelacionadosQuebec';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Guía financiera para latinos en Quebec',
  description:
    'Todo lo que necesitas saber sobre finanzas en Quebec: Revenu Quebec, Solidarity Tax Credit, QPP vs CPP, guarderías y más.',
  url: 'https://www.finmaple.com/quebec',
};

const sections = [
  {
    icon: <CompareArrows />,
    title: 'Revenu Quebec vs CRA',
    color: '#1E3A5F',
    content: `Si vives en Quebec, debes hacer <strong>dos declaraciones de impuestos</strong> cada año: una federal (T1 para la CRA) y una provincial (TP-1 para Revenu Quebec). Es la única provincia de Canadá donde esto ocurre.

Esto significa que necesitas dos números de referencia distintos, recibes dos avisos de evaluación y puedes deber o recibir reembolso por separado. La buena noticia es que software como Wealthsimple Tax genera ambas declaraciones automáticamente.

<strong>Diferencias clave:</strong>
<ul>
<li>Quebec tiene sus propias tablas de impuestos provinciales (generalmente más altas)</li>
<li>Algunos creditos federales no aplican en Quebec y viceversa</li>
<li>El QST (Quebec Sales Tax) es diferente al HST de otras provincias</li>
</ul>`,
  },
  {
    icon: <Receipt />,
    title: 'Solidarity Tax Credit',
    color: '#7D5A00',
    content: `El <strong>Solidarity Tax Credit</strong> es un beneficio exclusivo de Quebec que combina tres componentes en un solo pago mensual:

<ul>
<li><strong>Componente QST:</strong> Hasta ~$340/ano - compensa el impuesto de ventas de Quebec</li>
<li><strong>Componente vivienda:</strong> Hasta ~$700/ano - para inquilinos o propietarios</li>
<li><strong>Componente municipios nordicos:</strong> Monto variable - para residentes de zonas remotas</li>
</ul>

Para recibirlo, solo necesitas declarar impuestos provinciales (TP-1). Revenu Quebec calcula automáticamente si calificas. Es un beneficio <strong>libre de impuestos</strong> que se deposita mensualmente.`,
  },
  {
    icon: <AccountBalance />,
    title: 'QPP vs CPP',
    color: '#1E3A5F',
    content: `Quebec tiene su propio plan de pensiones: el <strong>QPP (Quebec Pension Plan)</strong>, que reemplaza al CPP federal. Ambos funcionan de manera similar, pero con tasas ligeramente diferentes.

<strong>Cómo funciona:</strong>
<ul>
<li>Tanto tu como tu empleador contribuyen un porcentaje de tu salario</li>
<li>La tasa combinada del QPP es ~12.8% (dividida entre empleado y empleador)</li>
<li>Puedes comenzar a recibir pension desde los 60 años (monto reducido) o esperar hasta los 70 (monto aumentado)</li>
<li>La edad estandar para recibir el 100% es 65 años</li>
</ul>

Si te mudas de Quebec a otra provincia (o viceversa), tus contribuciones se transfieren entre QPP y CPP sin perder nada.`,
  },
  {
    icon: <ChildCare />,
    title: 'Guarderias en Quebec',
    color: '#6A1B9A',
    content: `Quebec tiene el sistema de guarderías más accesible de Canadá gracias a los <strong>CPE (Centres de la petite enfance)</strong> y guarderías subvencionadas.

<strong>Costo:</strong> Las plazas subvencionadas cuestan <strong>$9.10/dia</strong> por hijo (2026). Es significativamente más barato que el resto de Canadá donde puede costar $50-80/dia.

<strong>Como acceder:</strong>
<ul>
<li>Registrate en <strong>La Place 0-5</strong> (laplace0-5.com) - es la lista de espera centralizada</li>
<li>Registrate lo antes posible, incluso durante el embarazo</li>
<li>Las listas de espera pueden ser de 1-2 años dependiendo de la zona</li>
<li>Mientras esperas, las guarderías privadas no subvencionadas ofrecen un crédito fiscal provincial que reduce significativamente el costo</li>
</ul>`,
  },
];

const faqs = [
  {
    question: 'Necesito hacer dos declaraciones de impuestos en Quebec?',
    answer:
      'Si. Quebec es la única provincia donde debes declarar tanto a nivel federal (CRA, formulario T1) como provincial (Revenu Quebec, formulario TP-1). Software como Wealthsimple Tax genera ambas automáticamente.',
  },
  {
    question: 'El Solidarity Tax Credit reemplaza al GST Credit?',
    answer:
      'No completamente. El componente QST del Solidarity Tax Credit es el equivalente provincial, pero sigues recibiendo el GST/HST Credit federal de la CRA por separado.',
  },
  {
    question: 'Puedo transferir mi pension QPP si me mudo a otra provincia?',
    answer:
      'Si. Tus contribuciones al QPP se reconocen en el CPP y viceversa. No pierdes nada al mudarte entre provincias.',
  },
  {
    question: '¿Cuánto cuestan las guarderías en Quebec?',
    answer:
      'Las plazas subvencionadas en CPE cuestan $9.10/dia (2026). Las guarderías privadas cuestan más pero ofrecen un crédito fiscal provincial que reduce significativamente el costo real.',
  },
  {
    question: 'Es más caro vivir en Quebec por los impuestos?',
    answer:
      'Quebec tiene tasas provinciales más altas, pero ofrece más servicios: guarderías a $9.10/dia, universidad más barata, seguro de medicamentos universal, y multiples creditos fiscales como el Solidarity Tax Credit.',
  },
];

const QuebecPage = () => {
  const { i18n } = useTranslation();
  const { lng } = useParams();
  return (
    <Box sx={{ bgcolor: '#F7F9FC', minHeight: '100vh' }}>
      <SEOHead
        seoKey="quebec"
        title="Guía financiera Quebec - Impuestos, QPP, guarderías para latinos"
        description="Guía completa de finanzas en Quebec para latinos: Revenu Quebec vs CRA, Solidarity Tax Credit, QPP vs CPP, guarderías a $9.10/dia y más."
        canonical="/quebec"
        routeKey="quebec"
        schema={schema}
        noIndex={i18n.language !== 'es'}
      />

      <PageHeader
        variant="functional"
        title="Guía financiera para Quebec"
        subtitle="Todo lo que necesitas saber sobre impuestos, beneficios y servicios en la Belle Province."
        breadcrumbs={[{ label: 'Quebec' }]}
      >
        <Typography variant="overline" sx={{ color: '#5F6B7A', mt: 1.5, display: 'block' }}>FinMaple Quebec</Typography>
      </PageHeader>

      <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>

        <AtomicAnswer text="Quebec tiene su propio sistema fiscal con Revenu Quebec, el plan de pensiones QPP, guarderías a $9.10/dia y el Solidarity Tax Credit. Es la única provincia donde haces dos declaraciones de impuestos: federal (CRA) y provincial." />

        {/* Topic Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {sections.map((section, i) => (
            <Grid item xs={12} key={i}>
              <Card
                sx={{
                  borderRadius: '14px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  borderLeft: `4px solid ${section.color}`,
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    {React.cloneElement(section.icon, {
                      sx: { fontSize: 28, color: section.color },
                    })}
                    <Typography variant="h4" sx={{ color: '#1A1A1A' }}>
                      {section.title}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      '& p, & li': {
                        color: '#1A1A1A',
                        lineHeight: 1.8,
                        fontSize: '1rem',
                      },
                      '& ul': { pl: 2.5, mb: 2 },
                      '& li': { mb: 0.5 },
                      '& strong': { color: '#1A1A1A' },
                    }}
                    dangerouslySetInnerHTML={{ __html: section.content }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Artículos Quebec */}
        <ArticulosRelacionadosQuebec />

        {/* Related Resources */}
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '14px',
            p: { xs: 3, md: 4 },
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            mb: 4,
          }}
        >
          <Typography variant="h4" sx={{ color: '#1A1A1A', mb: 2 }}>
            Recursos adicionales
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Button
                component={RouterLink}
                to={`/${lng}/financlopedia/revenu-quebec-canada`}
                fullWidth
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  borderColor: '#1E3A5F',
                  color: '#1E3A5F',
                  borderRadius: '10px',
                  py: 1.5,
                  justifyContent: 'flex-start',
                  '&:hover': { bgcolor: '#F7F9FC' },
                }}
              >
                Diccionario: Revenu Quebec
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                component={RouterLink}
                to={`/${lng}/calendario-fiscal-2026`}
                fullWidth
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  borderColor: '#1E3A5F',
                  color: '#1E3A5F',
                  borderRadius: '10px',
                  py: 1.5,
                  justifyContent: 'flex-start',
                  '&:hover': { bgcolor: '#F7F9FC' },
                }}
              >
                Calendario fiscal 2026
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                component={RouterLink}
                to={`/${lng}/empieza-aqui`}
                fullWidth
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  borderColor: '#6A1B9A',
                  color: '#6A1B9A',
                  borderRadius: '10px',
                  py: 1.5,
                  justifyContent: 'flex-start',
                  '&:hover': { bgcolor: '#F3E5F5' },
                }}
              >
                Primeros pasos en Canadá
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* FAQs */}
        <FAQSection faqs={faqs} title="Preguntas frecuentes sobre Quebec" />

        {/* Disclaimer */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="body2" sx={{ color: '#5F6B7A', fontStyle: 'italic', lineHeight: 1.6 }}>
          La información de esta página es educativa y puede cambiar. Consulta revenuquebec.ca y canada.ca para datos oficiales actualizados.
        </Typography>
      </Container>
    </Box>
  );
};

export default QuebecPage;
